/**
 * The one scene-icon vocabulary.
 *
 * BMS-spec `docs/edge/rest-api.md` §"Scene icon vocabulary" and
 * `schemas/scene-icon.v1.schema.json`. `sceneIcons.contract.test.ts` checks this
 * export against that schema, so the two cannot drift.
 *
 * These are **semantic concepts, not icon-library identifiers**. A renderer maps
 * a name to whatever glyph it draws; nothing here names a package, an import, or
 * a URL, and a reader must never render a stored value as one.
 *
 * Authoring and reading are deliberately asymmetric:
 *
 * - **Authoring** is closed. A new or explicitly edited icon outside this set is
 *   `400 invalid_request`, and an authoring UI offers these choices and explains
 *   the invalid field before submitting.
 * - **Reading** is open. A stored value from a future Edge, or from an older one
 *   that allowed something else, renders as a generic scene glyph with the scene
 *   name visible and an accessible `Activate scene: {name}` label.
 *
 * The asymmetry is the point: a stored unknown value is data that already
 * exists, and refusing to display it would break a working room. Silently
 * rewriting it would destroy an authoring intent nobody asked to change — so an
 * unrelated partial edit leaves it alone, and only an explicit edit of that
 * field must resolve it.
 */
/** Omission, `null` and `""` all mean "generic / no selected icon". */
export const GENERIC_SCENE_ICON = null;
export const SCENE_ICONS = [
    'sun',
    'moon',
    'movie',
    'dinner',
    'party',
    'sleep',
    'meeting',
    'welcome',
    'away',
    'night',
    'morning',
    'reading',
    'relax',
    'bright',
    'dim',
    'off',
    'eco',
    'comfort',
    'cleaning',
];
/**
 * Whether `value` is one of the supported names.
 *
 * Case-sensitive, as the contract requires: `Sun` is not `sun`, and accepting it
 * would let two spellings of one concept into storage.
 */
export function isSupportedSceneIcon(value) {
    return typeof value === 'string' && SCENE_ICONS.includes(value);
}
/** Whether `value` means "no selected icon". */
export function isGenericSceneIcon(value) {
    return value === null || value === undefined || value === '';
}
/**
 * Whether an authoring submission is acceptable.
 *
 * Use this before `POST`/`PUT`, so the field can be explained in place rather
 * than surfacing as a server refusal the operator cannot act on.
 */
export function isValidSceneIconForAuthoring(value) {
    return isGenericSceneIcon(value) || isSupportedSceneIcon(value);
}
/**
 * What a **reader** should draw for a stored value.
 *
 * Returns the supported name, or `null` meaning "generic glyph". An unknown
 * stored name is not an error here and is never returned for rendering — the
 * caller draws the generic glyph and shows the scene's name.
 */
export function sceneIconForDisplay(value) {
    return isSupportedSceneIcon(value) ? value : null;
}
/**
 * Whether a stored value is present but outside the vocabulary.
 *
 * Authoring surfaces use this to warn and require an explicit choice; readers
 * use it for nothing at all, because to a reader an unknown icon is simply
 * generic.
 */
export function isUnknownStoredSceneIcon(value) {
    return !isGenericSceneIcon(value) && !isSupportedSceneIcon(value);
}
//# sourceMappingURL=sceneIcons.js.map